import { Component, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, MessageCircle, X } from 'lucide-react'
import Home from './pages/Home.jsx'
import Catalogo from './pages/Catalogo.jsx'
import Carro from './pages/Carro.jsx'
import Documentos from './pages/Documentos.jsx'
import Investir from './pages/Investir.jsx'
import InvestirDados from './pages/InvestirDados.jsx'
import MeusCarros from './pages/MeusCarros.jsx'
import Admin from './pages/Admin.jsx'

// Rede de segurança: se algum erro de runtime derrubar a árvore do React,
// mostra o erro na tela em vez de deixar tudo branco (essencial pra
// diagnosticar em celular, onde não há devtools).
class ErrorBoundary extends Component {
  state = { erro: null }
  static getDerivedStateFromError(erro) {
    return { erro }
  }
  render() {
    if (!this.state.erro) return this.props.children
    return (
      <main className="mx-auto max-w-md px-4 py-16 text-center">
        <div className="font-display text-xl font-bold text-slate-900">Ops, algo deu errado.</div>
        <p className="mt-2 text-sm text-slate-500">Recarregue a página. Se continuar, mande um print desta tela:</p>
        <pre className="mt-4 overflow-x-auto rounded-xl bg-slate-100 p-4 text-left text-xs text-red-600">
          {String(this.state.erro?.stack || this.state.erro)}
          {'\n\n' + navigator.userAgent}
        </pre>
        <button
          onClick={() => window.location.reload()}
          className="mt-5 rounded-xl bg-brand-600 px-6 py-3 text-sm font-semibold text-white"
        >
          Recarregar
        </button>
      </main>
    )
  }
}

function ScrollToTop() {
  const { pathname } = useLocation()
  // Chaves obrigatórias: em alguns Androids window.scrollTo retorna um valor
  // truthy, e um effect que "vaza" esse retorno faz o React chamá-lo como
  // cleanup na navegação seguinte → TypeError e tela branca no aparelho.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

const navLinks = [
  ['/carros', 'Carros'],
  ['/investir', 'Investir'],
  ['/meus-carros', 'Meus carros'],
  ['/admin', 'Admin'],
]

function Nav() {
  const [aberto, setAberto] = useState(false)
  const { pathname } = useLocation()
  useEffect(() => {
    setAberto(false)
  }, [pathname])
  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? 'text-brand-700' : 'text-slate-600 hover:text-slate-900'}`
  return (
    // Fundo sólido de propósito: backdrop-filter em elemento sticky/fixed
    // dispara bugs de rendering (tela branca) no Safari do iOS 26.
    <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-2 px-4 md:h-16">
        <Link to="/" className="font-display text-xl font-extrabold tracking-tight text-brand-700 md:text-2xl">
          Sim<span className="text-amber-500">.</span>
        </Link>
        <nav className="hidden items-center gap-6 md:flex">
          {navLinks.map(([to, label]) => <NavLink key={to} to={to} className={linkCls}>{label}</NavLink>)}
        </nav>
        <div className="flex items-center gap-1">
          <Link
            to="/carros"
            className="rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 active:bg-brand-700 sm:text-sm md:px-5 md:py-2.5"
          >
            Quero meu carro
          </Link>
          <button
            onClick={() => setAberto((v) => !v)}
            aria-label={aberto ? 'Fechar menu' : 'Abrir menu'}
            className="-mr-2 rounded-full p-2.5 text-slate-600 active:bg-slate-100 md:hidden"
          >
            {aberto ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {aberto && (
        <nav className="border-t border-slate-100 bg-white px-2 py-2 shadow-lg shadow-slate-900/5 md:hidden">
          {navLinks.map(([to, label]) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `block rounded-xl px-4 py-3 text-sm font-semibold ${isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-700 active:bg-slate-50'}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  )
}

function Footer() {
  return (
    <footer className="mt-10 border-t border-slate-200 bg-white md:mt-20">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-4 py-8 md:grid-cols-4 md:gap-8 md:py-12">
        <div className="col-span-2 md:col-span-1">
          <div className="font-display text-2xl font-extrabold text-brand-700">Sim<span className="text-amber-500">.</span></div>
          <p className="mt-2 text-sm text-slate-500">
            Alugue, dirija e o carro fica seu. Seguro e manutenção por nossa conta.
          </p>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">Para você</div>
          <ul className="mt-3 space-y-2 text-sm text-slate-500">
            <li><Link to="/carros" className="hover:text-brand-700">Ver carros</Link></li>
            <li><Link to="/#como-funciona" className="hover:text-brand-700">Como funciona</Link></li>
            <li><Link to="/meus-carros" className="hover:text-brand-700">Meus carros</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">Para investir</div>
          <ul className="mt-3 space-y-2 text-sm text-slate-500">
            <li><Link to="/investir" className="hover:text-brand-700">Invista com a Sim</Link></li>
            <li><Link to="/investir" className="hover:text-brand-700">Consigne seu carro</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">Contato</div>
          <ul className="mt-3 space-y-2 text-sm text-slate-500">
            <li>WhatsApp: (11) 99999-0000</li>
            <li>contato@simcarros.com.br</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-100 py-4 text-center text-xs text-slate-400">
        Protótipo de demonstração — dados fictícios · Seus dados são tratados conforme a LGPD
      </div>
    </footer>
  )
}

const SEEN_KEY = 'sim_views'
const PROMPT_KEY = 'sim_prompts'
const LEAD_KEY = 'sim_lead'

// Chrome com cookies/dados de site bloqueados lança SecurityError ao ACESSAR
// window.sessionStorage — sem o try/catch, o throw dentro do useEffect do
// LiteRegSheet desmontava o app inteiro (tela branca a cada navegação).
const store = {
  get(k) {
    try {
      return window.sessionStorage.getItem(k)
    } catch {
      return null
    }
  },
  set(k, v) {
    try {
      window.sessionStorage.setItem(k, v)
    } catch {
      /* sem storage, o cadastro leve simplesmente não aparece */
    }
  },
}

function LiteRegSheet() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [sent, setSent] = useState(false)
  const timerRef = useRef(null)

  const canPrompt = () =>
    !store.get(LEAD_KEY) && Number(store.get(PROMPT_KEY) || 0) < 2

  const prompt = () => {
    if (!canPrompt()) return
    store.set(PROMPT_KEY, String(Number(store.get(PROMPT_KEY) || 0) + 1))
    setOpen(true)
  }

  useEffect(() => {
    clearTimeout(timerRef.current)
    const isCarPage = /^\/carros\/.+/.test(location.pathname)
    if (!isCarPage) return
    const views = Number(store.get(SEEN_KEY) || 0) + 1
    store.set(SEEN_KEY, String(views))
    if (views >= 3 && views % 3 === 0) prompt()
    else timerRef.current = setTimeout(prompt, 45000)
    return () => clearTimeout(timerRef.current)
  }, [location.pathname])

  if (!open) return null
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-5">
      <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
        {sent ? (
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-brand-50 p-2 text-brand-700"><MessageCircle size={20} /></div>
            <div>
              <div className="font-display font-bold">Combinado! 💬</div>
              <div className="text-sm text-slate-500">Vamos te chamar no WhatsApp pra ajudar na escolha.</div>
            </div>
            <button onClick={() => setOpen(false)} className="ml-auto rounded-full p-2 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg font-bold">Quer ajuda pra escolher?</div>
                <p className="mt-1 text-sm text-slate-500">
                  Deixa seu WhatsApp que a gente te chama — sem compromisso.
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="rounded-full p-2 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
            </div>
            <form
              className="mt-4 flex flex-col gap-2 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault()
                store.set(LEAD_KEY, '1')
                setSent(true)
              }}
            >
              <input required placeholder="Seu nome" className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-base outline-none sm:text-sm focus:border-brand-600" />
              <input required placeholder="WhatsApp (DDD + número)" className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-base outline-none sm:text-sm focus:border-brand-600" />
              <button className="rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700">
                Pode chamar
              </button>
            </form>
            <p className="mt-2 text-[11px] text-slate-400">
              Ao enviar, você concorda em ser contatado pela Sim (LGPD).
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Nav />
        <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/carros" element={<Catalogo />} />
        <Route path="/carros/:id" element={<Carro />} />
        <Route path="/documentos" element={<Documentos />} />
        <Route path="/investir" element={<Investir />} />
        <Route path="/investir/dados" element={<InvestirDados />} />
        <Route path="/meus-carros" element={<MeusCarros />} />
        <Route path="/admin" element={<Admin />} />
        </Routes>
        <Footer />
        <LiteRegSheet />
      </ErrorBoundary>
    </BrowserRouter>
  )
}
